import {
  Button,
  DatePicker,
  DatePickerProps,
  DatePickerValue,
  DropdownButtonContext,
  FlexBox,
} from '@dixa/lib-shared-habla';
import moment from 'moment';
import { memo, useCallback, useContext } from 'react';

import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './conversationFollowUpDatePicker.module.scss';

type ConversationFollowUpDatePickerProps = {
  onConfirm: VoidFunction;
  onCancel?: VoidFunction;
} & Pick<DatePickerProps, 'date' | 'presets' | 'onChange'>;

export const ConversationFollowUpDatePicker = memo(
  function ConversationFollowUpDatePicker({
    date,
    presets,
    onChange,
    onCancel,
    onConfirm,
  }: ConversationFollowUpDatePickerProps): JSX.Element {
    const { closeDropdown } = useContext(DropdownButtonContext);

    const { pending: followUpAsyncStatus } = useAppSelector(
      (state) => state.conversation.asyncStatus,
    );

    const handleDateChange = useCallback(
      (value: DatePickerValue) => {
        onChange(value);

        // Close the dropdown immediately only when a preset is selected
        // Otherwise, the dropdown will close when the confirm button is clicked
        if (value.presetKey) {
          closeDropdown();
        }
      },
      [closeDropdown, onChange],
    );

    const handleCancel = useCallback(() => {
      onCancel?.();
      closeDropdown();
    }, [closeDropdown, onCancel]);

    const handleConfirm = useCallback(() => {
      onConfirm();
      closeDropdown();
    }, [closeDropdown, onConfirm]);

    return (
      <>
        <DatePicker
          date={date}
          onChange={handleDateChange}
          withDateInput
          withTimePicker
          inputsPlacement="top"
          minDate={moment()}
          presets={presets}
          timeRoundingStrategy="minute"
        />

        <FlexBox
          justifyContent="flex-end"
          gap="250"
          padding="250"
          className={styles.actions}
        >
          {onCancel && (
            <Button
              variant="tertiary"
              onClick={handleCancel}
              isLoading={followUpAsyncStatus === 'loading'}
            >
              Remove follow up
            </Button>
          )}

          <Button variant="primary" disabled={!date} onClick={handleConfirm}>
            Confirm
          </Button>
        </FlexBox>
      </>
    );
  },
);
